// achievements, points and medals. every achievement is one step on a ladder that counts one habit, so adding a habit
// is one entry here and one place that bumps its counter (awards_service.ts). points only ever come from achievements

export type AwardCategory = "notes" | "plan" | "studio" | "upskill" | "social" | "habit" | "explore";

export interface CategoryInfo {
  id: AwardCategory;
  label: string;
  icon: string;
}

export const AWARD_CATEGORIES: CategoryInfo[] = [
  { id: "notes", label: "Notes", icon: "notes" },
  { id: "plan", label: "Planning", icon: "tasks" },
  { id: "studio", label: "Studio", icon: "gesture" },
  { id: "upskill", label: "Upskill", icon: "japanese" },
  { id: "social", label: "Social", icon: "board" },
  { id: "habit", label: "Habits", icon: "repeat" },
  { id: "explore", label: "Explorer", icon: "star" }
];

interface Ladder {
  counter: string;
  category: AwardCategory;
  icon: string;
  // what the goal reads like for one and for {n}
  what: [string, string];
  steps: Array<[number, string]>;
  // a secret one shows as a question mark until it is earned
  secret?: boolean;
}

// points for the first, second, third... step of a ladder
const STEP_POINTS = [10, 25, 50, 100, 200, 350, 500, 750, 1000];

const LADDERS: Ladder[] = [
  // notes and library
  { counter: "notes_made", category: "notes", icon: "notes", what: ["write a note", "write {n} notes"], steps: [[1, "First sticky"], [10, "Sticky pad"], [50, "Paper trail"], [150, "Notebook full"], [400, "Archivist"], [1000, "Library of you"]] },
  { counter: "inbox_jots", category: "notes", icon: "plus", what: ["jot one thing to the inbox", "jot {n} things to the inbox"], steps: [[1, "Quick jot"], [10, "Inbox open"], [50, "Brain dump"], [200, "Capture machine"], [500, "Nothing escapes"]] },
  { counter: "voice_used", category: "notes", icon: "mic", what: ["dictate something", "dictate {n} times"], steps: [[1, "Say it out loud"], [10, "Dictation habit"], [50, "Talks to the pocket"], [150, "Voice of the desk"]] },
  { counter: "scans_saved", category: "notes", icon: "scan", what: ["save a scan", "save {n} scans"], steps: [[1, "Flatbed first"], [10, "Paper to pixels"], [50, "Scan lab"], [200, "Digitiser"]] },
  { counter: "tierlists_made", category: "notes", icon: "star", what: ["make a tier list", "make {n} tier lists"], steps: [[1, "Ranked"], [5, "Opinionated"], [20, "Tier tyrant"]] },
  { counter: "tracks_added", category: "notes", icon: "play", what: ["add a track to the player", "add {n} tracks to the player"], steps: [[1, "Needle drop"], [10, "Playlist maker"], [50, "Crate digger"]] },

  // planning
  { counter: "tasks_made", category: "plan", icon: "tasks", what: ["add a task", "add {n} tasks"], steps: [[1, "Writes it down"], [10, "Task wrangler"], [50, "Backlog boss"], [200, "Project manager"]] },
  { counter: "tasks_done", category: "plan", icon: "check", what: ["finish a task", "finish {n} tasks"], steps: [[1, "One down"], [10, "On a roll"], [50, "Closer"], [200, "Finisher"], [500, "Unstoppable"], [1000, "Legend of done"]] },
  { counter: "routines_done", category: "plan", icon: "repeat", what: ["tick off a routine", "tick off {n} routines"], steps: [[1, "Routine starter"], [10, "Habit forming"], [50, "Clockwork"], [200, "Ritual master"], [500, "Metronome"], [1000, "Eternal"]] },
  { counter: "goals_made", category: "plan", icon: "goal", what: ["set a goal", "set {n} goals"], steps: [[1, "Aimed high"], [5, "Goal getter"], [15, "Dreamer"]] },
  { counter: "goals_done", category: "plan", icon: "goal", what: ["reach a goal", "reach {n} goals"], steps: [[1, "Goal reached"], [5, "Bullseye"], [15, "Achiever"], [40, "Overachiever"]] },
  { counter: "pings_set", category: "plan", icon: "bell", what: ["set a ping", "set {n} pings"], steps: [[1, "Remind me"], [10, "Never forgets"], [50, "Alarm collector"], [200, "Time lord"]] },
  { counter: "events_added", category: "plan", icon: "calendar", what: ["add a calendar event", "add {n} calendar events"], steps: [[1, "Pencilled in"], [10, "Busy bee"], [50, "Calendar king"], [150, "Fully booked"]] },
  { counter: "places_saved", category: "plan", icon: "map", what: ["save a place", "save {n} places"], steps: [[1, "Pinned a spot"], [5, "Local guide"], [20, "Cartographer"]] },
  { counter: "budget_items", category: "plan", icon: "budget", what: ["add a budget item", "add {n} budget items"], steps: [[1, "Counting coins"], [10, "Budget keeper"], [40, "Ledger lord"]] },
  { counter: "budget_paid", category: "plan", icon: "budget", what: ["log a payment", "log {n} payments"], steps: [[1, "Paid up"], [10, "Bills beaten"], [50, "Debt dodger"], [150, "Clear conscience"]] },

  // studio
  { counter: "gesture_poses", category: "studio", icon: "gesture", what: ["draw {n} gesture poses", "draw {n} gesture poses"], steps: [[5, "Warm up"], [50, "Gesture regular"], [200, "Fast hands"], [500, "Pose machine"], [1500, "Thousand poses"]] },
  { counter: "gesture_sessions", category: "studio", icon: "timer", what: ["start a gesture session", "start {n} gesture sessions"], steps: [[1, "First session"], [10, "Studio rat"], [40, "Daily drawer"], [100, "Gesture guru"]] },
  { counter: "challenges_done", category: "studio", icon: "doodle", what: ["finish a drawing challenge", "finish {n} drawing challenges"], steps: [[1, "Challenge accepted"], [5, "Warm up habit"], [15, "Sketchbook regular"], [30, "Month of marks"], [60, "Mileage"], [100, "Hundred drawings"]] },
  { counter: "challenge_refs", category: "studio", icon: "refs", what: ["draw a suggested ref", "draw {n} suggested refs"], steps: [[1, "Reference hunter"], [10, "Study buddy"], [40, "Ref master"]] },
  { counter: "drawings_posted", category: "studio", icon: "camera", what: ["share a drawing on the feed", "share {n} drawings on the feed"], steps: [[1, "Show and tell"], [5, "Gallery wall"], [20, "Regular exhibitor"], [50, "Solo show"]] },
  { counter: "challenge_streak", category: "studio", icon: "repeat", what: ["finish the challenge {n} days in a row", "finish the challenge {n} days in a row"], steps: [[3, "Three days drawn"], [7, "Week of marks"], [14, "Fortnight of lines"], [30, "Ink month"], [60, "Callus on the finger"]] },
  { counter: "comms_added", category: "studio", icon: "jobs", what: ["log a commission", "log {n} commissions"], steps: [[1, "First commission"], [5, "Getting booked"], [20, "Open for comms"], [60, "Busy artist"]] },
  { counter: "payments_logged", category: "studio", icon: "budget", what: ["log a commission payment", "log {n} commission payments"], steps: [[1, "Paid!"], [10, "Rent covered"], [40, "Pro artist"], [100, "Money flows"]] },
  { counter: "prompts_added", category: "studio", icon: "prompts", what: ["save a prompt", "save {n} prompts"], steps: [[1, "Idea seed"], [10, "Prompt bank"], [40, "Never a blank page"]] },
  { counter: "colors_saved", category: "studio", icon: "palette", what: ["save a colour", "save {n} colours"], steps: [[1, "Swatch"], [10, "Palette lover"], [40, "Colour theory"]] },
  { counter: "ocs_saved", category: "studio", icon: "ocs", what: ["make a character sheet", "make {n} character sheets"], steps: [[1, "Meet the cast"], [5, "Character builder"], [20, "Worldbuilder"], [50, "Universe"]] },
  { counter: "refs_described", category: "studio", icon: "refs", what: ["describe a reference", "describe {n} references"], steps: [[1, "Describer"], [10, "Curator"], [50, "Librarian"], [200, "Master curator"]] },
  { counter: "doodles_drawn", category: "studio", icon: "doodle", what: ["add a doodle to the wall", "add {n} doodles to the wall"], steps: [[1, "First scribble"], [10, "Wall decorator"], [50, "Doodle machine"], [200, "Wall of fame"]] },
  { counter: "whiteboards_made", category: "studio", icon: "whiteboard", what: ["start a whiteboard", "start {n} whiteboards"], steps: [[1, "Blank canvas"], [5, "Brainstormer"], [20, "Board room"]] },

  // upskill
  { counter: "cards_reviewed", category: "upskill", icon: "japanese", what: ["answer a flashcard", "answer {n} flashcards"], steps: [[1, "First card"], [25, "Getting the basics"], [100, "Hundred reviews"], [500, "Flash fan"], [2000, "Anki would be proud"], [5000, "Fluent-ish"], [10000, "Ten thousand cards"]] },
  { counter: "languages_tried", category: "upskill", icon: "web", what: ["study a language", "study {n} languages"], steps: [[1, "Hello"], [2, "Bilingual-ish"], [3, "Polyglot trainee"], [5, "Polyglot"], [8, "Tower of Babel"]] },
  { counter: "study_days", category: "upskill", icon: "calendar", what: ["study on a day", "study on {n} different days"], steps: [[1, "Day one"], [7, "A week of words"], [30, "Month of study"], [100, "Centurion"], [365, "Year of languages"]] },

  // social
  { counter: "chats", category: "social", icon: "ask", what: ["ask chi something", "ask chi {n} things"], steps: [[1, "Hello chi"], [10, "Chatterbox"], [50, "Best friends"], [200, "Soulmates"], [1000, "Inseparable"]] },
  { counter: "board_posts", category: "social", icon: "board", what: ["post on the board", "post on the board {n} times"], steps: [[1, "First post"], [10, "Regular"], [50, "Board voice"], [200, "Town crier"]] },
  { counter: "suggestions_posted", category: "social", icon: "spark", what: ["suggest a change to Chix", "suggest {n} changes to Chix"], steps: [[1, "Idea box"], [5, "Product manager"], [15, "Roadmap shaper"]] },
  { counter: "suggestions_voted", category: "social", icon: "updown", what: ["vote on a suggestion", "vote on {n} suggestions"], steps: [[1, "Cast a vote"], [10, "Democracy"], [40, "Kingmaker"]] },
  { counter: "suggestions_shipped", category: "social", icon: "check", what: ["have a suggestion built", "have {n} suggestions built"], steps: [[1, "You built Chix"], [3, "Co-author"], [10, "Core contributor"]] },
  { counter: "shares_set", category: "social", icon: "share", what: ["share something with someone", "share {n} things"], steps: [[1, "Sharing is caring"], [5, "Open book"], [10, "Open house"]] },
  { counter: "profile_saved", category: "social", icon: "people", what: ["fill in your profile", "fill in your profile"], steps: [[1, "About me"]] },

  // habits
  { counter: "active_days", category: "habit", icon: "today", what: ["open Chix on a day", "open Chix on {n} different days"], steps: [[1, "Welcome"], [3, "Regular"], [7, "A week in"], [14, "Fortnight"], [30, "A month in"], [60, "Two months"], [100, "Century"], [200, "Devoted"], [365, "Year of Chi"]] },
  { counter: "streak_best", category: "habit", icon: "repeat", what: ["open Chix {n} days in a row", "open Chix {n} days in a row"], steps: [[2, "Back again"], [3, "Three-peat"], [7, "Perfect week"], [14, "Two weeks strong"], [30, "Iron month"], [60, "Unbroken"], [100, "Hundred day run"], [200, "Relentless"], [365, "Full orbit"]] },
  { counter: "night_days", category: "habit", icon: "star", secret: true, what: ["use Chix in the small hours", "use Chix in the small hours on {n} days"], steps: [[1, "Night owl"], [5, "Vampire hours"], [20, "Nocturnal"]] },
  { counter: "early_days", category: "habit", icon: "today", secret: true, what: ["use Chix at dawn", "use Chix at dawn on {n} days"], steps: [[1, "Early bird"], [5, "Dawn patrol"], [20, "Sunrise club"]] },
  { counter: "weekend_days", category: "habit", icon: "calendar", what: ["use Chix on {n} weekend days", "use Chix on {n} weekend days"], steps: [[2, "Weekend warrior"], [10, "Saturday regular"], [30, "No days off"]] },
  { counter: "medals_cleaned", category: "habit", icon: "spark", what: ["polish a dirty medal", "polish {n} dirty medals"], steps: [[1, "Polish"], [10, "Shiny"], [40, "Spotless"], [100, "Obsessive polisher"], [250, "Medal valet"]] },

  // explorer
  { counter: "features_used", category: "explore", icon: "grid", what: ["try {n} parts of Chix", "try {n} parts of Chix"], steps: [[3, "Sampler"], [6, "Explorer"], [10, "Power user"], [15, "Everything bagel"]] },
  { counter: "home_tweaks", category: "explore", icon: "settings", what: ["rearrange home on a day", "rearrange home on {n} days"], steps: [[1, "Rearranged"], [5, "Interior designer"], [20, "Rack architect"]] },
  { counter: "look_changes", category: "explore", icon: "palette", what: ["change the look of Chix", "change the look {n} times"], steps: [[1, "New look"], [5, "Fashionista"]] },
  { counter: "roulette_spins", category: "explore", icon: "roulette", what: ["spin a wheel", "spin a wheel {n} times"], steps: [[1, "Spin it"], [10, "Fortune"], [50, "Gambler"]] },
  { counter: "medals_earned", category: "explore", icon: "star", what: ["earn a medal", "earn {n} medals"], steps: [[1, "First medal"], [5, "Display case"], [10, "Trophy cabinet"], [20, "Hall of fame"]] },
  { counter: "points", category: "explore", icon: "chart", what: ["reach {n} points", "reach {n} points"], steps: [[100, "Century"], [500, "Half a thousand"], [1000, "Grand"], [2500, "High roller"], [5000, "Master"], [10000, "Mythic"]] }
];

export interface Achievement {
  id: string;
  name: string;
  desc: string;
  category: AwardCategory;
  icon: string;
  counter: string;
  target: number;
  points: number;
  secret: boolean;
  // which step of its ladder this is, 0 first
  tier: number;
}

export const ACHIEVEMENTS: Achievement[] = LADDERS.flatMap((ladder) => ladder.steps.map(([target, name], tier): Achievement => ({
  id: `${ladder.counter}-${target}`,
  name,
  desc: ladder.what[target === 1 ? 0 : 1].replace("{n}", target.toLocaleString("en")),
  category: ladder.category,
  icon: ladder.icon,
  counter: ladder.counter,
  target,
  points: STEP_POINTS[Math.min(tier, STEP_POINTS.length - 1)],
  secret: Boolean(ladder.secret),
  tier
})));

export const COUNTERS = Array.from(new Set(ACHIEVEMENTS.map((item) => item.counter)));

export type Metal = "bronze" | "silver" | "gold" | "crystal";

// what a medal adds to the leaderboard while it is clean, a dirty one adds less
export const METAL_SHINE: Record<Metal, number> = { bronze: 10, silver: 25, gold: 60, crystal: 120 };

// the outline a medal is cut in. every area of chix has its own, specials are stars unless they say otherwise
export type MedalShape = "circle" | "hex" | "shield" | "star" | "octa" | "diamond" | "rosette" | "gear";

export interface MedalDef {
  id: string;
  name: string;
  desc: string;
  metal: Metal;
  icon: string;
  // earned when a category adds up to this many points, or when this one achievement is
  category?: AwardCategory;
  at?: number;
  achievement?: string;
  // or when every one of these medals is held
  all?: string[];
  // shows as a question mark until earned, like the secret achievement it comes from
  secret?: boolean;
  // which shelf of the medal case it hangs on, and its outline. both default from the category
  shelf?: AwardCategory | "special";
  shape?: MedalShape;
}

const SHAPE_OF_SHELF: Record<AwardCategory | "special", MedalShape> = {
  notes: "circle", plan: "hex", studio: "shield", upskill: "octa", social: "rosette", habit: "gear", explore: "diamond", special: "star"
};

export const medalShelf = (def: MedalDef): AwardCategory | "special" => def.shelf ?? def.category ?? "special";
export const medalShape = (def: MedalDef): MedalShape => def.shape ?? SHAPE_OF_SHELF[medalShelf(def)];

// a medal is not clean or dirty, it is covered in up to three layers that only one tool each can remove:
// rust (brush), then grime underneath (sponge), then a milky haze (cloth). 1 is fully covered, 0 is gone.
// dirt is the one number the server keeps (0 spotless, 1 filthy), these turn it into layers and back
export interface MedalLayers {
  rust: number;
  grime: number;
  haze: number;
}

const unit = (value: number): number => Math.min(1, Math.max(0, value));

export function layersOfDirt(dirt: number): MedalLayers {
  if (dirt <= 0.02) return { rust: 0, grime: 0, haze: 0 };
  return { rust: unit((dirt - 0.45) / 0.55), grime: unit((dirt - 0.12) / 0.33), haze: unit((dirt - 0.02) / 0.1) };
}

// the least dirt that still shows these layers, so a half polished medal keeps what is left of it
export function dirtOfLayers(layers: MedalLayers, done = 0.08): number {
  if (layers.rust > done) return 0.45 + 0.55 * unit(layers.rust);
  if (layers.grime > done) return 0.12 + 0.33 * unit(layers.grime);
  if (layers.haze > done) return 0.02 + 0.1 * unit(layers.haze);
  return 0;
}

const METAL_TIERS: Array<[Metal, number, string]> = [["bronze", 60, "Bronze"], ["silver", 250, "Silver"], ["gold", 700, "Gold"]];

const CATEGORY_MEDALS: MedalDef[] = AWARD_CATEGORIES.flatMap((category) => METAL_TIERS.map(([metal, at, label]): MedalDef => ({
  id: `${category.id}-${metal}`,
  name: `${category.label} ${label}`,
  desc: `${at} points from ${category.label.toLowerCase()} achievements`,
  metal,
  icon: category.icon,
  category: category.id,
  at
})));

const SPECIAL_MEDALS: MedalDef[] = [
  { id: "streak-week", name: "Perfect Week", desc: "open Chix 7 days in a row", metal: "bronze", icon: "repeat", achievement: "streak_best-7" },
  { id: "streak-month", name: "Iron Month", desc: "open Chix 30 days in a row", metal: "silver", icon: "repeat", achievement: "streak_best-30" },
  { id: "streak-hundred", name: "Hundred Day Run", desc: "open Chix 100 days in a row", metal: "gold", icon: "repeat", achievement: "streak_best-100" },
  { id: "polyglot", name: "Polyglot", desc: "study 5 languages", metal: "silver", icon: "web", achievement: "languages_tried-5" },
  { id: "babel", name: "Tower of Babel", desc: "study 8 languages", metal: "gold", icon: "web", achievement: "languages_tried-8" },
  { id: "inked", name: "Ink Month", desc: "finish the drawing challenge 30 days in a row", metal: "gold", icon: "doodle", achievement: "challenge_streak-30" },
  { id: "builder", name: "You Built Chix", desc: "have a suggestion built", metal: "silver", icon: "spark", achievement: "suggestions_shipped-1" },
  { id: "mythic", name: "Mythic", desc: "reach 10,000 points", metal: "crystal", icon: "star", achievement: "points-10000" }
];

// one medal per milestone of a ladder: [counter-target, metal, name, icon]. metal b/s/g/c is bronze/silver/gold/crystal.
// a long ladder (5+ steps) gets a third, two thirds and the top, a 3-4 step one the second last and the top, a short one just the top
const LADDER_MEDALS: Array<[string, "b" | "s" | "g" | "c", string, string]> = [
  ["notes_made-50", "b", "Scribbler", "notes"], ["notes_made-400", "s", "Quill Keeper", "edit"], ["notes_made-1000", "g", "Grand Scribe", "trophy"],
  ["inbox_jots-10", "b", "Pocket Catch", "plus"], ["inbox_jots-200", "s", "Net Caster", "filter"], ["inbox_jots-500", "g", "Bottomless Pocket", "download"],
  ["voice_used-50", "s", "Whisperer", "mic"], ["voice_used-150", "g", "Orator", "feed"],
  ["scans_saved-50", "s", "Flatbed Pro", "scan"], ["scans_saved-200", "g", "Paper Alchemist", "print"],
  ["tierlists_made-5", "s", "Hot Takes", "tierlist"], ["tierlists_made-20", "g", "Supreme Judge", "sort"],
  ["tracks_added-10", "s", "Mixtape", "music"], ["tracks_added-50", "g", "Vinyl Vault", "play"],

  ["tasks_made-50", "s", "Taskmaster", "tasks"], ["tasks_made-200", "g", "Grand Planner", "jobs"],
  ["tasks_done-50", "b", "Box Ticker", "check"], ["tasks_done-500", "s", "Relentless", "power"], ["tasks_done-1000", "g", "Thousand Ticks", "trophy"],
  ["routines_done-50", "b", "Cog Turner", "repeat"], ["routines_done-500", "s", "Pendulum", "timer"], ["routines_done-1000", "g", "Perpetual Motion", "refresh"],
  ["goals_made-5", "s", "Sharpshooter", "goal"], ["goals_made-15", "g", "Visionary", "eye"],
  ["goals_done-15", "s", "Dead Centre", "goal"], ["goals_done-40", "g", "Marksman", "trophy"],
  ["pings_set-50", "s", "Bell Ringer", "bell"], ["pings_set-200", "g", "Clock Whisperer", "timer"],
  ["events_added-50", "s", "Date Keeper", "calendar"], ["events_added-150", "g", "Almanac", "today"],
  ["places_saved-5", "s", "Wayfinder", "pin"], ["places_saved-20", "g", "Mapmaker", "map"],
  ["budget_items-10", "s", "Penny Pincher", "budget"], ["budget_items-40", "g", "Treasurer", "chart"],
  ["budget_paid-50", "s", "Debt Slayer", "calc"], ["budget_paid-150", "g", "Square Books", "budget"],

  ["gesture_poses-50", "b", "Quick Draw", "gesture"], ["gesture_poses-500", "s", "Pose Engine", "timer"], ["gesture_poses-1500", "g", "Life Drawer", "gesture"],
  ["gesture_sessions-40", "s", "Easel Regular", "picker"], ["gesture_sessions-100", "g", "Gesture Sage", "hand"],
  ["challenges_done-15", "b", "Daily Doodler", "doodle"], ["challenges_done-60", "s", "Page Turner", "next"], ["challenges_done-100", "g", "Hundred Studies", "trophy"],
  ["challenge_refs-10", "s", "Ref Seeker", "refs"], ["challenge_refs-40", "g", "Eagle Eye", "eye"],
  ["drawings_posted-20", "s", "Exhibitor", "camera"], ["drawings_posted-50", "g", "Gallery Owner", "feed"],
  ["challenge_streak-7", "b", "Seven Sketches", "repeat"], ["challenge_streak-60", "g", "Unbroken Line", "route"],
  ["comms_added-20", "s", "Open Slots", "jobs"], ["comms_added-60", "g", "Booked Solid", "calendar"],
  ["payments_logged-40", "s", "Paid In Full", "budget"], ["payments_logged-100", "g", "Cash Flow", "chart"],
  ["prompts_added-10", "s", "Spark Jar", "prompts"], ["prompts_added-40", "g", "Idea Hoard", "spark"],
  ["colors_saved-10", "s", "Paint Pot", "palette"], ["colors_saved-40", "g", "Rainbow Vault", "wb-rainbow"],
  ["ocs_saved-20", "s", "Cast Director", "ocs"], ["ocs_saved-50", "g", "World Weaver", "layers"],
  ["refs_described-50", "s", "Caption Pro", "edit"], ["refs_described-200", "g", "Grand Curator", "memory"],
  ["doodles_drawn-50", "s", "Margin Doodler", "wb-pencil"], ["doodles_drawn-200", "g", "Wall Legend", "wb-marker"],
  ["whiteboards_made-5", "s", "Chalk Dust", "wb-chalk"], ["whiteboards_made-20", "g", "Blank Slate", "whiteboard"],

  ["cards_reviewed-100", "b", "Card Shark", "japanese"], ["cards_reviewed-2000", "s", "Deck Demon", "layers"], ["cards_reviewed-10000", "g", "Memory Palace", "memory"],
  ["languages_tried-2", "b", "Code Switcher", "web"],
  ["study_days-7", "b", "Weekly Words", "calendar"], ["study_days-100", "s", "Daily Student", "today"], ["study_days-365", "g", "Long Scholar", "trophy"],

  ["chats-10", "b", "Small Talk", "ask"], ["chats-200", "s", "Confidant", "claude"], ["chats-1000", "g", "Kindred", "memory"],
  ["board_posts-50", "s", "Pinboard Pro", "board"], ["board_posts-200", "g", "Town Voice", "send"],
  ["suggestions_posted-5", "s", "Idea Smith", "spark"], ["suggestions_posted-15", "g", "Blueprint Maker", "route"],
  ["suggestions_voted-10", "s", "Ballot Box", "sort"], ["suggestions_voted-40", "g", "Swing Vote", "up"],
  ["suggestions_shipped-3", "s", "Co-Pilot", "link"], ["suggestions_shipped-10", "g", "Architect", "server"],
  ["shares_set-5", "s", "Generous", "share"], ["shares_set-10", "g", "Open Door", "open"],
  ["profile_saved-1", "g", "Face Card", "people"],

  ["active_days-14", "b", "Regular Face", "today"], ["active_days-100", "s", "Fixture", "pin"], ["active_days-365", "g", "Full Orbit", "refresh"],
  ["streak_best-14", "b", "Fortnight Flame", "spark"], ["streak_best-365", "g", "Year Unbroken", "trophy"],
  ["night_days-5", "s", "Moth", "star"], ["night_days-20", "g", "Night Shift", "power"],
  ["early_days-5", "s", "Lark", "today"], ["early_days-20", "g", "First Light", "eye"],
  ["weekend_days-10", "s", "Weekender", "calendar"], ["weekend_days-30", "g", "Seventh Day", "repeat"],
  ["medals_cleaned-10", "b", "Soft Cloth", "refresh"], ["medals_cleaned-100", "c", "Mirror Finish", "star"], ["medals_cleaned-250", "g", "Burnisher", "spark"],

  ["features_used-10", "s", "Tinkerer", "settings"], ["features_used-15", "g", "Full Rack", "grid"],
  ["home_tweaks-5", "s", "Shelf Stacker", "layers"], ["home_tweaks-20", "g", "Rack Wizard", "power"],
  ["look_changes-5", "g", "Chameleon", "palette"],
  ["roulette_spins-10", "s", "Lucky Streak", "roulette"], ["roulette_spins-50", "g", "High Stakes", "shuffle"],
  ["medals_earned-10", "s", "Collector", "trophy"], ["medals_earned-20", "g", "Glass Cabinet", "star"],
  ["points-1000", "b", "Four Digits", "chart"], ["points-5000", "c", "Five Grand", "chart"]
];

const METAL_OF: Record<"b" | "s" | "g" | "c", Metal> = { b: "bronze", s: "silver", g: "gold", c: "crystal" };

const LADDER_MEDAL_DEFS: MedalDef[] = LADDER_MEDALS.map(([id, metal, name, icon]): MedalDef => {
  const item = ACHIEVEMENTS.find((entry) => entry.id === id)!;
  const secret = LADDERS.find((ladder) => ladder.counter === item.counter)?.secret;
  return { id: `m-${id}`, name, desc: item.desc, metal: METAL_OF[metal], icon, achievement: id, shelf: item.category, ...(secret ? { secret: true } : {}) };
});

// held once all seven areas have their gold medal
const ALL_GOLD: MedalDef = {
  id: "all-gold", name: "Full Spectrum", desc: "hold the gold medal of every area", metal: "crystal", icon: "star",
  all: AWARD_CATEGORIES.map((category) => `${category.id}-gold`)
};

export const MEDALS: MedalDef[] = [...CATEGORY_MEDALS, ...SPECIAL_MEDALS, ...LADDER_MEDAL_DEFS, ALL_GOLD];

// points the daily drawing challenge pays straight away, on top of any achievement it unlocks.
// finishing the exercise is worth `done`, sharing the drawing on the feed is worth `drawing` more
export const CHALLENGE_POINTS = { done: 5, drawing: 10 };

// a medal is fully dirty after this long without a rub
export const GRIME_DAYS = 7;
export const GRIME_MS = GRIME_DAYS * 86_400_000;

// one counter a screen is allowed to bump on its own. everything else is counted where it happens
export const CLIENT_COUNTERS = ["voice_used"];

export interface AchievementState {
  id: string;
  progress: number;
  done: boolean;
  at: string;
}

export interface MedalState {
  id: string;
  earned: boolean;
  at: string;
  // 0 spotless to 1 filthy
  dirt: number;
}

export interface AwardChallengePick {
  id: string;
  title: string;
  how: string;
  minutes: number;
  source: string;
  link: string;
}

export interface AwardRefChoice {
  id: string;
  thumb: string;
  // the full size picture, for the lightbox
  image: string;
  author: string;
}

export interface ChallengeState {
  day: string;
  // the drawing shared today, if any, and whether there is one
  posted: boolean;
  image: string;
  mode: "exercise" | "ref" | "gesture";
  // gesture sessions started and poses picked since this day began, for the gesture option
  gesture: { sessions: number; poses: number };
  exercise: AwardChallengePick;
  refs: AwardRefChoice[];
  picked: string;
  done: boolean;
  streak: number;
}

export interface AwardsSummary {
  points: number;
  shine: number;
  score: number;
  unlocked: number;
  total: number;
  achievements: AchievementState[];
  medals: MedalState[];
  categories: Array<{ id: AwardCategory; points: number; max: number }>;
  visible: boolean;
  // this person's place on the board and how many are on it
  rank: number;
  people: number;
}

export interface BoardEntry {
  id: string;
  name: string;
  color: string;
  points: number;
  shine: number;
  score: number;
  streak: number;
  unlocked: number;
  medals: Array<{ id: string; metal: Metal; icon: string; dirt: number }>;
  // how the whole collection is kept, the row above only draws the best six
  held: number;
  shining: number;
  rusty: number;
  you: boolean;
}

export interface Unlock {
  achievements: Array<{ id: string; name: string; points: number }>;
  medals: Array<{ id: string; name: string }>;
  // how many medals were really earned when `medals` was folded into one summary line
  medalCount?: number;
}

export interface Suggestion {
  id: string;
  text: string;
  by: string;
  name: string;
  at: string;
  votes: number;
  voted: boolean;
  status: "open" | "planned" | "done" | "declined";
  note: string;
  mine: boolean;
}

// the main feed: what people post, drew, unlocked and studied, and what happens between them, newest first
export type FeedKind = "post" | "drawing" | "challenge" | "achievement" | "study" | "joined" | "shared";

export interface FeedData {
  // the exercise the drawing was for, or "a reference" for draw a ref
  title?: string;
  mode?: "exercise" | "ref" | "gesture";
  // poses picked in the gesture session, for a gesture drawing
  poses?: number;
  note?: string;
  streak?: number;
  achievements?: Array<{ name: string; points: number }>;
  medals?: string[];
  reviewed?: number;
  languages?: string[];
  // the ref someone drew, when they drew one
  ref?: string;
  // what someone wrote in a post
  text?: string;
  // a thing that was shared with everyone, a tier list or a whiteboard. its name is in title
  what?: "tier list" | "whiteboard";
}

export interface FeedComment {
  id: string;
  by: string;
  name: string;
  at: string;
  text: string;
  mine: boolean;
}

export interface FeedItem {
  id: string;
  kind: FeedKind;
  by: string;
  name: string;
  at: string;
  data: FeedData;
  // the picture someone shared, empty for everything else
  image: string;
  cheers: number;
  cheered: boolean;
  mine: boolean;
  comments: FeedComment[];
  // empty until the poster changes the text
  editedAt: string;
}
